import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { providersApiRefs } from '../../apis';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import { COLORS } from '@traton/common-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Page, UserIdentity } from '@backstage/core-components';
import {
  BackstageIdentityApi,
  configApiRef,
  IdentityApi,
  OAuthApi,
  ProfileInfoApi,
  SessionApi,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { TratonLogo } from '../logo';
import { GuestSupportPage } from '../guest';

type LoginState = 'idle' | 'loading' | 'error';

type ProviderId = 'microsoft' | 'ams';

type ProviderAuthApi = OAuthApi &
  ProfileInfoApi &
  BackstageIdentityApi &
  SessionApi;

type ProviderButtonVariant = 'contained' | 'outlined';

type ProviderConfig = {
  id: ProviderId;
  label: string;
  variant: ProviderButtonVariant;
};

type ProviderErrors = Partial<Record<ProviderId, string>>;

const PROVIDER_CONFIG: readonly ProviderConfig[] = [
  {
    id: 'microsoft',
    label: 'TRATON EMPLOYEES AND CONSULTANTS',
    variant: 'outlined',
  },
  {
    id: 'ams',
    label: 'PARTNERS AND ALL USERS IN CHINA',
    variant: 'outlined',
  },
] as const;

const GUEST_SUPPORT_PATH = '/guest/support';

export const CustomSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { pathname } = useLocation();
  const isGuestRoute = pathname === GUEST_SUPPORT_PATH;
  const config = useApi(configApiRef);

  const [loginState, setLoginState] = useState<LoginState>('idle');
  const [activeProvider, setActiveProvider] = useState<ProviderId | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(!isGuestRoute);
  const [providerErrors, setProviderErrors] = useState<ProviderErrors>({});

  const microsoftAuthApi = useApi(providersApiRefs.microsoftProviderAuthApiRef);
  const amsAuthApi = useApi(providersApiRefs.amsProviderAuthApiRef);

  const providerApis = useMemo<Record<ProviderId, ProviderAuthApi>>(
    () => ({
      microsoft: microsoftAuthApi,
      ams: amsAuthApi,
    }),
    [microsoftAuthApi, amsAuthApi],
  );

  const hasSignedInRef = useRef(false);
  const onSignInSuccessRef = useRef(onSignInSuccess);
  useEffect(() => {
    onSignInSuccessRef.current = onSignInSuccess;
  }, [onSignInSuccess]);

  const completeSignIn = useCallback((identity: IdentityApi) => {
    if (hasSignedInRef.current) return;
    hasSignedInRef.current = true;
    onSignInSuccessRef.current(identity);
  }, []);

  const buildIdentity = useCallback(
    async (
      authApi: ProviderAuthApi,
      options: { instantPopup?: boolean; optional?: boolean },
    ): Promise<IdentityApi | undefined> => {
      const identityResponse = options.optional
        ? await authApi.getBackstageIdentity({ optional: true })
        : await authApi.getBackstageIdentity({ instantPopup: true });

      if (!identityResponse) return undefined;

      // Profile is best-effort — a failure here should not block a valid sign-in.
      const profile = await authApi
        .getProfile(options.optional ? { optional: true } : {})
        .catch(() => undefined);

      return UserIdentity.create({
        identity: identityResponse.identity,
        authApi,
        profile: profile ?? undefined,
      });
    },
    [],
  );

  const signIn = useCallback(
    async (providerId: ProviderId) => {
      if (hasSignedInRef.current) return;

      setLoginState('loading');
      setActiveProvider(providerId);
      setProviderErrors(previous => ({ ...previous, [providerId]: undefined }));

      try {
        const identity = await buildIdentity(providerApis[providerId], {
          instantPopup: true,
        });

        if (!identity) {
          throw new Error('Sign-in completed without an identity response.');
        }

        completeSignIn(identity);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Sign-in failed for provider "${providerId}":`, error);

        setProviderErrors(previous => ({
          ...previous,
          [providerId]: "We couldn't log you in. Please try again.",
        }));
        setLoginState('error');
        setActiveProvider(null);
      }
    },
    [providerApis, buildIdentity, completeSignIn],
  );

  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return undefined;
    hasRestoredRef.current = true;

    if (isGuestRoute) {
      setIsCheckingSession(false);
      return undefined;
    }

    let mounted = true;

    const restoreSession = async () => {
      for (const { id } of PROVIDER_CONFIG) {
        if (hasSignedInRef.current || !mounted) return;

        try {
          const identity = await buildIdentity(providerApis[id], {
            optional: true,
          });

          if (!mounted) return;

          if (identity) {
            completeSignIn(identity);
            return;
          }
        } catch {
          // Ignore silent session restore errors and try the next provider.
        }
      }

      if (mounted) {
        setIsCheckingSession(false);
      }
    };

    restoreSession();

    return () => {
      mounted = false;
    };
    // Run exactly once — providerApis and callbacks are captured via refs / stable identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = config.getString('app.title');
  }, [config]);

  if (isGuestRoute) {
    return <GuestSupportPage />;
  }

  const isLoading = loginState === 'loading';
  const disableButtons = isCheckingSession || isLoading;
  const sectionTextColor = isDark
    ? COLORS.dark.TRATON_White
    : COLORS.light.TRATON_Warm_Grey_1100;

  if (isCheckingSession) {
    return (
      <Page themeId="home">
        <Box
          component="main"
          role="main"
          sx={{
            gridArea: 'pageContent',
            width: '100%',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            px: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 448, mb: 4 }}>
            <TratonLogo applyTheme width="100%" height="auto" maxWidth={448} />
          </Box>
          <CircularProgress aria-label="Checking session" />
        </Box>
      </Page>
    );
  }

  const getButtonLabel = (
    providerId: ProviderId,
    defaultLabel: string,
  ): string => {
    if (isCheckingSession) return 'Checking session...';
    if (isLoading && activeProvider === providerId) return 'Logging in...';
    return defaultLabel;
  };

  return (
    <Page themeId="home">
      <Box
        component="main"
        role="main"
        sx={{
          gridArea: 'pageContent',
          width: '100%',
          minHeight: '100dvh',
          boxSizing: 'border-box',
          px: { xs: 3, md: 3, lg: 0 },
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 10,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 448, mb: 8 }}>
          <TratonLogo applyTheme width="100%" height="auto" maxWidth={448} />
        </Box>
        <Card
          component="section"
          aria-live="polite"
          sx={{
            width: 448,
            maxWidth: '100%',
            borderRadius: 1,
            boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.20)',
          }}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 6 } }}>
            <Typography variant="h5" sx={{ lineHeight: 1 }}>
              Choose login option
            </Typography>

            {PROVIDER_CONFIG.map(({ id, label, variant }) => (
              <Box key={id} mt={4}>
                <Button
                  variant={variant}
                  fullWidth
                  onClick={() => signIn(id)}
                  disabled={disableButtons}
                >
                  {getButtonLabel(id, label)}
                </Button>

                {providerErrors[id] ? (
                  <Typography
                    variant="body2"
                    color="error"
                    role="alert"
                    sx={{ mt: 1.5 }}
                  >
                    {providerErrors[id]}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </CardContent>
        </Card>

        <Box
          mt={3}
          sx={{
            width: '100%',
            maxWidth: 448,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Link
            href="/guest/support"
            variant="body1"
            underline="always"
            sx={{
              color: sectionTextColor,
              textDecorationColor: sectionTextColor,
              textUnderlineOffset: '2px',
              lineHeight: 1,
              display: 'block',
              textAlign: 'center',
              '&:hover': {
                textDecorationColor: sectionTextColor,
              },
            }}
          >
            Need help or request access?
          </Link>
        </Box>
      </Box>
    </Page>
  );
};
